import { useState, useEffect, useRef } from "react";
import "../../../assets/css/channelBenchmarks.scss";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import DateRangePicker, {
  DateRangeData,
} from "../../../shared/components/dateRangePicker";
import { ZSSelectBox } from "../../../shared/components/selectBox";
import { QuickPresetsSelect } from "../../../shared/components/quickPresetsSelect";
import BenchmarkErrorPage from "../benchMarkError";
import { BenchMarkErrorType, BenchmarkScreenType } from "../../../shared/enum";
import { useLocalStorage } from "../../../contexts/LocalStorageContext";
import { fetchPeerSets } from "../../../shared/utils/fetchPeerSets";
import { CircularProgress } from "@mui/material";
import {
  generateColorBands,
  getColorFromBands,
  getTextColor,
} from "../../../shared/utils/heatmapHelper";
import PrintContainer from "../../../shared/components/printContainer";
import { ReportExportActions } from "../../../shared/components/reportExportButton";
// ─── Types ──────────────────────────────────────────────────────────────────────

interface MatrixRow {
  channel: string;
  revenue: number;
  conversions: number;
  roas: number;
  costConv: number;
  impShare: number | null;
  colorType: string;
}

interface InsightMetric {
  label: string;
  change: string;
  percent: string;
  value: number;
  trend: "up" | "down";
}

interface InsightCard {
  title: string;
  accentColor: string;
  metrics: InsightMetric[];
}

interface DashboardData {
  matrixData: MatrixRow[];
  insightsData: InsightCard[];
}

// ─── Tokens ────────────────────────────────────────────────────────────────────

const DARK_BG = "#1e2d24";

// ─── Styled ────────────────────────────────────────────────────────────────────

const DarkPaper = styled(Paper)({
  backgroundColor: DARK_BG,
  color: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
});

// ─── Static Data (JSON format) ─────────────────────────────────────────────────

const marketOptions = [
  { label: "Market + Size (Boston \u00b7 Mid)", value: "Boston \u00b7 Mid" },
  {
    label: "Market + Size (New York \u00b7 Large)",
    value: "New York \u00b7 Large",
  },
  { label: "Market + Size (Chicago \u00b7 Mid)", value: "Chicago \u00b7 Mid" },
];

const compareOptions = [
  { label: "YoY", value: "YoY" },
  { label: "QoQ", value: "QoQ" },
  { label: "MoM", value: "MoM" },
];

const peerSetOptions = [
  { label: "Quick Presets", value: "Quick Presets" },
  { label: "Custom", value: "Custom" },
];

const PRESETS = [
  {
    id: "market-size",
    title: "Market + Size",
    subtitles: ["Boston • Mid-size (100–199 Rooms)"],
  },
  {
    id: "urban-large",
    title: "Urban Large",
    subtitles: ["Boston • Large (200–499 Rooms)", "Urban Hotel"],
  },
  {
    id: "luxury-4-star",
    title: "Luxury 4-Star",
    subtitles: ["Boston • Mid-size (100–199 Rooms)", "4-Star"],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

// const COLOR_SCALE = [
//   "#A4605D",
//   "#D57F5B",
//   "#E8B282",
//   "#F3D49E",
//   "#B2CB9D",
//   "#689271",
//   "#395847",
// ];

const WHITE_TEXT_COLORS = new Set([
  "#689271",
  "#395847",
  "#B2CB9D",
  "#A4605D",
  "#D57F5B",
]);

// const roundDown5 = (n: number) => Math.floor(n / 5) * 5;
// const roundUp5 = (n: number) => Math.ceil(n / 5) * 5;
// const roundNearest5 = (n: number) => Math.round(n / 5) * 5;

// const getMetricTextColor = (bgColor: string): string =>
//   WHITE_TEXT_COLORS.has(bgColor) ? "#FFFFFF" : "#1E1E1E";

// const getCellStyles = (
//   value: number,
//   min: number,
//   max: number,
// ): React.CSSProperties => {
//   const bg = getMetricColor(value, min, max);
//   console.log("bgbg", bg);
//   return { backgroundColor: bg, color: getMetricTextColor(bg) };
// };

// const getMetricColor = (
//   value: number,
//   min: number,
//   max: number,
//   inverted: boolean = false,
// ): string => {
//   const neutral = 100;
//   let index = 3;

//   const clampedVal = Math.min(Math.max(value, min), max);

//   if (clampedVal < neutral) {
//     const range = neutral - min;
//     const factor = range <= 0 ? 0 : (clampedVal - min) / range;
//     index = Math.min(Math.floor(factor * 3), 2);
//   } else if (clampedVal > neutral) {
//     const range = max - neutral;
//     const factor = range <= 0 ? 0 : (clampedVal - neutral) / range;
//     index = Math.min(Math.floor(factor * 3) + 4, 6);
//   }

//   const finalIndex = inverted ? COLOR_SCALE.length - 1 - index : index;
//   return COLOR_SCALE[finalIndex];
// };

const getMetricRanges = (
  data: MatrixRow[],
): Record<string, { min: number; max: number }> => {
  const metrics = [
    "revenue",
    "conversions",
    "roas",
    "costConv",
    "impShare",
  ] as const;
  const ranges: Record<string, { min: number; max: number }> = {};
  for (const key of metrics) {
    const values = data
      ?.map((row) => row[key])
      ?.filter((v): v is number => v !== null && v !== undefined);
    ranges[key] = {
      min: values?.length ? Math?.min(...values) : 0,
      max: values?.length ? Math?.max(...values) : 0,
    };
  }
  return ranges;
};

export const getCardBottomColor = (title: string): string => {
  const colorMap: Record<string, string> = {
    Search: "#EFC893",
    Metasearch: "#ED9277",
    Display: "#CCDCAF",
    Social: "#CCDCAF",
  };
  return colorMap[title] || "#EFC893";
};

// ─── Main ──────────────────────────────────────────────────────────────────────

const ChannelBenchmarksReport = () => {
  const { reports } = useLocalStorage();

  const [dateRange, setDateRange] = useState<DateRangeData>(() => {
    const today = new Date();

    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 29);

    return {
      from: {
        year: pastDate.getFullYear(),
        month: pastDate.getMonth() + 1,
        day: pastDate.getDate(),
      },
      to: {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
      },
    };
  });
  const [loading, setLoading] = useState(false);
  const [market, setMarket] = useState("Boston \u00b7 Mid");
  const [compare, setCompare] = useState("YoY");
  const [selectedPeerSet, setSelectedPeerSet] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    matrixData: [],
    insightsData: [],
  });
  const [peerSets, setPeerSets] = useState<
    { id: string; title: string; subtitles: string[] }[]
  >([]);
  const [cohortPropertyCount, setCohortPropertyCount] = useState(0);
  const [errorType, setErrorType] = useState<BenchMarkErrorType>(
    BenchMarkErrorType.INVALID_FILTER,
  );
  const reportRef = useRef<HTMLDivElement>(null);

  const handleDateChange = (v: DateRangeData) => setDateRange(v);
  const handleResetFilter = () => {};

  const LAYER_BASE_URL = process.env.REACT_APP_API_LAYER_URL;

  const { propertyId, propertyTitle } = useLocalStorage();

  useEffect(() => {
    const loadPeerSets = async () => {
      if (!propertyId) return;
      setLoading(true);
      const { peerSets: fetched, defaultPeerSet } =
        await fetchPeerSets(propertyId);
      setPeerSets(fetched);
      if (defaultPeerSet) {
        setSelectedPeerSet({
          id: defaultPeerSet.id,
          title: defaultPeerSet.title,
        });
      }
    };
    loadPeerSets();
  }, [propertyId]);

  const fetchChannelBenchmarks = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }
    if (!selectedPeerSet?.id) {
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sourcePropertyId: String(propertyId),
        startDate: `${dateRange.from.year}-${String(dateRange.from.month).padStart(2, "0")}-${String(dateRange.from.day).padStart(2, "0")}`,
        endDate: `${dateRange.to!.year}-${String(dateRange.to!.month).padStart(2, "0")}-${String(dateRange.to!.day).padStart(2, "0")}`,
      });

      if (selectedPeerSet?.id) {
        params.append("peerSetId", selectedPeerSet.id);
      }

      const response = await fetch(
        `${LAYER_BASE_URL}/channel-spend-bench-mark?${params.toString()}`,
      );

      const data = await response.json();

      if (!response.ok || data?.error) {
        const count = data?.error?.details?.cohort_property_count ?? 0;
        const code = data?.error?.code;
        setCohortPropertyCount(count);
        setErrorType(
          code === "INSUFFICIENT_COHORT"
            ? BenchMarkErrorType.NOT_ENOUGH_PEER
            : BenchMarkErrorType.INVALID_FILTER,
        );
        setDashboardData({ matrixData: [], insightsData: [] });
        return;
      }

      setCohortPropertyCount(0);
      setErrorType(BenchMarkErrorType.INVALID_FILTER);
      setDashboardData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!reports) {
      return;
    }

    // Benchmark report is not available
    if (reports.benchmarkReportsCount === 0) {
      setDashboardData({
        matrixData: [],
        insightsData: [],
      });
      setLoading(false);
      return;
    }

    // Benchmark report is available
    if (reports && reports.benchmarkReportsCount > 0) {
      fetchChannelBenchmarks();
    }
  }, [propertyId, dateRange, selectedPeerSet, reports]);

  const { matrixData, insightsData } = dashboardData;
  const metricRanges = getMetricRanges(matrixData);

  const insightMetricValues = insightsData?.flatMap((card) =>
    card?.metrics?.map((m) => m.value),
  );
  const insightMin = insightMetricValues?.length
    ? Math.min(...insightMetricValues)
    : 0;
  const insightMax = insightMetricValues?.length
    ? Math.max(...insightMetricValues)
    : 0;

  const allValues = matrixData
    .flatMap((row) => [
      row.revenue,
      row.conversions,
      row.roas,
      // row.costConv,
      row.impShare,
    ])
    .filter((v): v is number => v !== null && v !== undefined);

  const rawMin = allValues.length ? Math.min(...allValues) : 70;
  const rawMax = allValues.length ? Math.max(...allValues) : 130;

  const costConvValue = matrixData
    .flatMap((row) => [row.costConv])
    .filter((v): v is number => v !== null && v !== undefined);

  const costConvMinVal = Math.round(
    costConvValue.length ? Math.min(...costConvValue) : 70,
  );
  const costConvMaxVal = Math.round(
    costConvValue.length ? Math.max(...costConvValue) : 130,
  );
  // const displayMin = roundDown5(rawMin);
  // const displayMax = roundUp5(rawMax);
  const displayMin = Math.round(rawMin);
  const displayMax = Math.round(rawMax);

  // const generateLabels = () => {
  //   const labels: number[] = [];

  //   const lowerStep = (100 - displayMin) / 4;
  //   for (let i = 0; i < 4; i++) {
  //     const val = displayMin + lowerStep * i;
  //     labels.push(i === 0 ? displayMin : roundNearest5(val));
  //   }

  //   labels.push(100);

  //   const upperStep = (displayMax - 100) / 4;
  //   for (let i = 1; i <= 4; i++) {
  //     const val = 100 + upperStep * i;
  //     labels.push(i === 4 ? displayMax : roundNearest5(val));
  //   }

  //   return labels;
  // };

  // const labels = generateLabels();
  const colorBands = generateColorBands(displayMin, displayMax);
  const invertedColorBands = generateColorBands(
    costConvMinVal,
    costConvMaxVal,
    true,
  );

  // console.log("labels ::::::::: ", displayMin, displayMax, colorBands);
  // console.log(
  //   "invertedColorBands ::::::::: ",
  //   displayMin,
  //   displayMax,
  //   invertedColorBands,
  // );

  const generateCSV = () => {
    // Define Headers based on the requirement for fields like Spend, ROAS, Cost/Conv, etc.
    const headers = [
      "Section",
      "Channel",
      "Metric",
      "Value (vs Peer)",
      "Peer Difference",
      "% Change",
      "Trend",
    ];

    const rows: any[][] = [];

    // 1. Add Heatmap Matrix Section
    matrixData.forEach((row) => {
      // We create a row for each channel showing its benchmark values
      // Following the requirement to include specific fields
      rows.push([
        "Benchmark Matrix",
        row.channel,
        "Revenue Score",
        row.revenue,
        "",
        "",
        "",
      ]);
      rows.push([
        "Benchmark Matrix",
        row.channel,
        "Conversions Score",
        row.conversions,
        "",
        "",
        "",
      ]);
      rows.push([
        "Benchmark Matrix",
        row.channel,
        "ROAS Score",
        row.roas,
        "",
        "",
        "",
      ]);
      rows.push([
        "Benchmark Matrix",
        row.channel,
        "Cost/Conversion Score",
        row.costConv,
        "",
        "",
        "",
      ]);
      if (row.impShare !== null) {
        rows.push([
          "Benchmark Matrix",
          row.channel,
          "Impression Share Score",
          row.impShare,
          "",
          "",
          "",
        ]);
      }
    });

    // Add an empty row as a separator
    rows.push([]);

    // 2. Add Channel Insights Section (Peer Comparisons)
    insightsData.forEach((card) => {
      card.metrics.forEach((metric) => {
        // Exclude Impression Share for Social/Display if needed based on UI logic
        if (
          (card.title === "Social" || card.title === "Display") &&
          metric.label === "Impression Share"
        ) {
          return;
        }

        rows.push([
          "Channel Insights",
          card.title,
          metric.label,
          metric.value, // The benchmark value
          metric.change, // e.g., "-$4.20"
          metric.percent, // e.g., "12%"
          metric.trend, // "up" or "down"
        ]);
      });
    });

    return { headers, rows };
  };

  const { headers: csvHeaders, rows: csvRows } = generateCSV();

  return (
    <Box
      /* Add this class here to match the CSS above */
      ref={reportRef}
      className="benchmarks-report-root"
      sx={{ bgcolor: "transparent", minHeight: "100vh", py: 4, px: 5, pt: 1 }}
    >
      <PrintContainer
        title="Channel Benchmarks Report"
        propertyTitle={propertyTitle}
      >
        <Container maxWidth={false} disableGutters>
          {/* ── Header ── */}
          <Stack className="benchmarks-header">
            <Typography className="benchmarks-heading">
              Channel Benchmarks
            </Typography>
          </Stack>
          {/* Filters + Export */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Stack direction="row" gap="12px" alignItems="center">
              <Box className="control-group">
                <span style={{ marginTop: "1px" }} className="date-range-label">
                  Date Range:
                </span>
                <DateRangePicker
                  dateRange={dateRange}
                  onChange={handleDateChange}
                  setDateRange={setDateRange}
                  setFilterData={handleResetFilter}
                  inputClassName="dateRangePickerInput"
                />
              </Box>
              {/* <Box className="control-group">
              <span className="date-range-label">Compare:</span>
              <div className="compare-select">
                <ZSSelectBox
                  fieldProps={{ id: "compare", name: "compare", placeholder: "Compare" }}
                  options={compareOptions}
                  value={compare}
                  onChange={(v: string) => setCompare(v)}
                />
              </div>
            </Box> */}
              <Box className="control-group">
                <span className="date-range-label">Peer Set:</span>
                <QuickPresetsSelect
                  presets={peerSets}
                  selectedId={selectedPeerSet?.id}
                  onSelect={(preset) => setSelectedPeerSet(preset)}
                  placeholder={selectedPeerSet?.title}
                />
              </Box>
              {/* <Button variant="outlined" className="btn btn-secondary" sx={{ textTransform: "none" }}>
              Customize Peer Set
            </Button> */}
            </Stack>
            <ReportExportActions
              filename="Channel_Benchmarks"
              csvHeaders={csvHeaders}
              csvData={csvRows}
              componentRef={reportRef}
              loading={loading}
            />
          </Stack>
          {loading && (
            <Paper className="card-panel matrix-card" elevation={0}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="70vh"
              >
                <CircularProgress />
              </Box>
            </Paper>
          )}
          {!matrixData?.length || !insightsData?.length ? (
            <BenchmarkErrorPage
              currentPeerCount={cohortPropertyCount}
              errorType={errorType}
              screenType={BenchmarkScreenType.CHANNEL_BENCHMARK}
            />
          ) : (
            <>
              {/* Heatmap Matrix Card */}
              <Paper className="card-panel matrix-card" elevation={0}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#43463B",
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "normal",
                    mb: 2,
                    marginLeft: "0px !important",
                  }}
                >
                  Heatmap Matrix
                </Typography>
                <Box className="matrix-table-wrapper">
                  <table className="matrix-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Revenue</th>
                        <th>Conversions</th>
                        <th>ROAS</th>
                        <th>Cost/Conversion</th>
                        <th>Impression Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixData?.map((row, idx) => (
                        <tr key={idx}>
                          <td className="channel-name">{row.channel}</td>
                          {[
                            { key: "revenue", val: row.revenue },
                            { key: "conversions", val: row.conversions },
                            { key: "roas", val: row.roas },
                            { key: "costConv", val: row.costConv },
                            { key: "impShare", val: row.impShare },
                          ].map((col) => {
                            // const isCost = col.key == "costConv" ? true : false;
                            // const band =
                            //   isCost == true ? invertedColorBands : colorBands;

                            const isCost = false;

                            const band = colorBands;

                            const bg =
                              col.val !== null
                                ? getColorFromBands(col.val, band)
                                : "transparent";

                            return (
                              <td
                                key={col.key}
                                className="cell"
                                style={{
                                  backgroundColor: bg,
                                  color: getTextColor(bg),
                                }}
                              >
                                {col.val ?? ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                {/* Legend Gradient Bar */}
                <Box className="legend-container">
                  <Box className="gradient-bar" />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    className="gradient-labels"
                  >
                    {colorBands.map((label, i) => (
                      <span
                        key={i}
                        style={{
                          // transform: "rotate(-30deg)",
                          display: "inline-block",
                        }}
                      >
                        {label?.value}
                      </span>
                    ))}
                  </Stack>
                </Box>
              </Paper>

              {/* Channel Insights Section */}
              <Paper
                className="card-panel matrix-card"
                elevation={0}
                sx={{ mt: 3 }}
              >
                <Box className="insights-section">
                  <Typography variant="h6" className="channel-insights-title">
                    Channel Insights
                  </Typography>

                  <Grid
                    container
                    // spacing={2}
                    // maxWidth="xl"
                    // justifyContent="space-between"
                    sx={{
                      mx: "auto",
                      // width: "calc(100% + 44px) !important",
                      // ml: "-20px !important",
                    }}
                    // spacing={2}
                    // maxWidth="xl"
                    // sx={{
                    //   ml: "20px",
                    //   mr: "20px",

                    //   width: "100%",
                    //   "& > .MuiGrid-item": { pl: "13px" },
                    // }}
                  >
                    {insightsData?.map((card, idx) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={5.7}
                        lg={3.9}
                        xl={3.9}
                        key={idx}
                        className="insight-card-wrapper"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          // minWidth:
                          //   insightsData.length === 1
                          //     ? "85% !important"
                          //     : insightsData.length === 2
                          //       ? "45% !important"
                          //       : "30% !important",
                          overflow: "hidden",
                        }}
                      >
                        <Paper
                          className="insight-card"
                          elevation={0}
                          sx={
                            {
                              "--accent-color": card.accentColor,
                              height: "100%",
                            } as React.CSSProperties
                          }
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={2.5}
                          >
                            {card.title}
                          </Typography>
                          {card.metrics.map((metric, mIdx) => {
                            if (
                              (card.title === "Social" ||
                                card.title === "Display") &&
                              metric.label === "Impression Share"
                            )
                              return null;
                            else
                              return (
                                <Box key={mIdx} className="metric-row">
                                  <Typography className="metric-label">
                                    {metric.label.replace(/\//g, "/\u200B")}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Typography className="peer-diff">
                                      {metric.change}
                                    </Typography>
                                    <Typography
                                      className={`percent-change ${metric.trend}`}
                                    >
                                      {metric.trend === "up" ? (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="13"
                                          height="14"
                                          viewBox="0 0 14 15"
                                          fill="none"
                                        >
                                          <path
                                            d="M7.27072 0.294969C6.84736 -0.098323 6.15983 -0.098323 5.73648 0.294969L0.317517 5.32911C-0.105839 5.7224 -0.105839 6.3611 0.317517 6.7544C0.740873 7.14769 1.4284 7.14769 1.85176 6.7544L5.4215 3.43501V13.088C5.4215 13.6449 5.90582 14.0948 6.50529 14.0948C7.10476 14.0948 7.58908 13.6449 7.58908 13.088V3.43501L11.1588 6.75125C11.5822 7.14454 12.2697 7.14454 12.6931 6.75125C13.1164 6.35796 13.1164 5.71925 12.6931 5.32596L7.27411 0.291823L7.27072 0.294969Z"
                                            fill="#5C8769"
                                          />
                                        </svg>
                                      ) : (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="13"
                                          height="14"
                                          viewBox="0 0 14 15"
                                          fill="none"
                                        >
                                          <path
                                            d="M7.27072 13.7998C6.84736 14.193 6.15983 14.193 5.73648 13.7998L0.317517 8.76562C-0.105839 8.37233 -0.105839 7.73362 0.317517 7.34033C0.740873 6.94704 1.4284 6.94704 1.85176 7.34033L5.4215 10.6597V1.00675C5.4215 0.449853 5.90582 -7.34329e-05 6.50529 -7.34329e-05C7.10476 -7.34329e-05 7.58908 0.449853 7.58908 1.00675V10.6597L11.1588 7.34348C11.5822 6.95018 12.2697 6.95018 12.6931 7.34348C13.1164 7.73677 13.1164 8.37547 12.6931 8.76877L7.27411 13.8029L7.27072 13.7998Z"
                                            fill="#991B1B"
                                          />
                                        </svg>
                                      )}
                                      {metric.percent}
                                    </Typography>
                                  </Stack>
                                  <Box className="metric-value-container">
                                    <Typography className="metric-value-channel-benchmark">
                                      {metric.value}
                                    </Typography>
                                    <Box
                                      className="metric-bar"
                                      sx={{
                                        backgroundColor: getColorFromBands(
                                          metric.value,
                                          // metric.label == "Cost/Conversion"
                                          //   ? invertedColorBands
                                          //   : colorBands,
                                          colorBands,
                                        ),
                                      }}
                                    />
                                  </Box>
                                </Box>
                              );
                          })}
                        </Paper>
                        <Box
                          className="card-bottom-line"
                          sx={{ background: getCardBottomColor(card.title) }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </>
          )}
        </Container>
      </PrintContainer>
    </Box>
  );
};

export default ChannelBenchmarksReport;
